import PropTypes from 'prop-types';
import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useImperativeHandle,
  useCallback,
  useLayoutEffect,
} from 'react';

import cls from 'classnames/bind';

import _identity from 'lodash/identity';
import _noop from 'lodash/noop';

import { deprecatedPropType } from '@ott/react-utils';

import useInputChange from './hooks/useInputChange';
import getMaskText from './utility/getMaskText';
import { parseValue, formatValue } from './utils';

import styles from './InputNext.scss';

function getInputAutoCompleteAttributeValue(enableAutoComplete, autoCompleteName) {
  if (!enableAutoComplete) {
    return 'off';
  }

  if (autoCompleteName) {
    return autoCompleteName;
  }

  return 'on';
}

const Input = React.memo(function InputNext(props) {
  const {
    className,
    childrenClassNames = {},
    size,
    placeholder,
    initial,
    value,
    readOnly,
    form,

    mask,
    tip,
    children,

    withTitle,
    isCVV,
    isError,

    parse,
    format,
    formatter,

    onFocus,
    onBlur,
    onKeyDown,
    onChange,
    onClick = _noop,
    withFullMask,
    forwardedRef,
    shouldIgnoreCursor,
    specificLocators,
    enableAutoComplete,
    autoCompleteName,
    icon,
    ...inputProps
  } = props;

  const [isFocused, setFocusState] = useState(false);
  const [isClient, setIsClientFlag] = useState(false);

  if (!inputProps.hasOwnProperty('autoComplete')) {
    inputProps.autoComplete = getInputAutoCompleteAttributeValue(
      enableAutoComplete,
      autoCompleteName
    );
  }

  const options = useMemo(
    () => ({
      isFocused,
      parse,
      format,
      formatter,
      onChange,
    }),
    [isFocused, parse, format, formatter, onChange]
  );

  const [inputInitialValue] = useState(() => {
    const initialValue = (typeof initial === 'string' ? initial : value) || '';

    const parsedValue = parseValue(options, initialValue);
    const formattedValue = formatValue(options, initialValue, parsedValue).value;

    // после formatValue пришедшее значение могло измениться
    const parsedNewValue = parseValue(options, formattedValue);
    if (formattedValue !== parsedNewValue && onChange) {
      onChange(parsedNewValue);
    }

    return formattedValue;
  });

  const inputRef = useRef(null);
  const prevValueRef = useRef(inputInitialValue);
  const [inputValue, setInputValue] = useState(inputInitialValue);

  const onChangeCallback = useInputChange({
    inputRef,
    prevValueRef,
    setInputValue,
    shouldIgnoreCursor,
    options,
  });

  const onFocusCallback = useCallback(
    (event) => {
      setFocusState(true);
      if (onFocus) {
        onFocus(event);
      }
    },
    [onFocus]
  );

  const onBlurCallback = useCallback(
    (event) => {
      setFocusState(false);
      if (onBlur) {
        onBlur(parseValue(props, event.target.value), event);
      }
    },
    [onBlur, parse]
  );

  const handleKeyDown = (e) => {
    const { name } = inputProps;
    onKeyDown && onKeyDown(e, name);
  };

  useImperativeHandle(
    forwardedRef,
    () => ({
      blur: () => {
        inputRef.current.blur();
      },
      focus: (params) => {
        inputRef.current.focus(params);
      },
      select: () => {
        inputRef.current.select();
        inputRef.current.setSelectionRange(0, inputRef.current.value.length); // iOS fix
      },
    }),
    [forwardedRef, inputRef]
  );

  // проверка на autoFocus={true}
  useEffect(() => {
    if (document.activeElement === inputRef.current) {
      setFocusState(true);
    }
  }, [inputRef]);

  // при измененнии value в props (из вне)
  // курсор не обрабатывается. если фокус на инпуте, то курсор слетит в конец
  useLayoutEffect(() => {
    let parsedValue = parseValue(options, value);
    const newValue = formatValue(options, value, parsedValue).value;

    if (inputValue !== newValue) {
      inputRef.current.value = newValue;
      prevValueRef.current = newValue;
      setInputValue(newValue);

      // после formatValue пришедшее значение могло измениться
      parsedValue = parseValue(options, newValue);
    }

    if (value !== parsedValue && onChange) {
      onChange(parsedValue);
    }
  }, [value, inputRef, prevValueRef, setInputValue, options]);

  useEffect(() => {
    setIsClientFlag(true);
  });

  const isEmpty = !inputValue;
  const isSmall = size === 'small';

  const hasValue = !isEmpty;
  const hasIcon = Boolean(icon);
  const hasMask = Boolean(!isSmall && mask);

  const showMask = hasMask && !isSmall && (isFocused || hasValue);
  const showTitle = !isSmall && withTitle;
  const showPlaceholder = isClient && !hasValue && !showMask && !(showTitle && isFocused);

  return (
    <div
      className={cls(
        styles.InputNext,
        className,
        isSmall && styles.InputNextIsSmall,
        inputProps.disabled && styles.InputNextIsDisabled,
        isError && styles.InputNextIsError,
        isFocused && !isError && !inputProps.disabled && styles.InputNextIsFocused,
        showTitle && styles.InputNextIsTitled,
        hasValue && styles.InputNextHasValue,
        hasIcon && styles.InputNextHasIcon,
        showPlaceholder && styles.InputNextShowPlaceholder
      )}
      data-locator={cls('Input', specificLocators)}
      onClick={onClick}
    >
      <label className={cls(styles.label, childrenClassNames.label)}>
        <input
          ref={inputRef}
          {...inputProps}
          form={form}
          defaultValue={inputInitialValue}
          onChange={onChangeCallback}
          onFocus={onFocusCallback}
          onBlur={onBlurCallback}
          onKeyDown={handleKeyDown}
          className={cls(styles.input, childrenClassNames.input, {
            [styles.isSecure]: isCVV,
          })}
          readOnly={readOnly}
          placeholder=""
          data-error-type="new"
          data-error-placement="top"
          data-error={isError ? 'true' : 'false'}
        />
        <div className={cls(styles.placeholder, childrenClassNames.placeholder)}>{placeholder}</div>
        {hasMask && (
          <input
            tabIndex="-1"
            value={getMaskText(inputValue, mask, withFullMask)}
            className={cls(styles.mask, { [styles['mask--show']]: showMask })}
            readOnly={true}
          />
        )}
        {hasIcon && <div className={cls(styles.icon, childrenClassNames.icon)}>{icon}</div>}
        {children && React.cloneElement(children, { isFocused, isError, value: inputValue })}
      </label>
      {tip}
    </div>
  );
});

Input.defaultProps = {
  parse: _identity,
  format: _identity,

  size: 'basic',
  tip: null,
  withTitle: true,
  withFullMask: false,
  type: 'text',
  enableAutoComplete: false,
  autoCompleteName: '',
  icon: null,
};

Input.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  initial: PropTypes.string,

  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.oneOfType([() => null, PropTypes.elementType]),
    }),
  ]),
  children: PropTypes.element,

  // eslint-disable-next-line react/forbid-prop-types
  isError: PropTypes.bool,

  /** parse = (inputValue, options) => parsedValue . */
  parse: PropTypes.func,
  /** format = (parsedValue, options) => formattedValue . */
  format: PropTypes.func,
  /** formatter = (inputValue, prevValue, cursor, options) => { value: formattedValue, cursor: newCursor } . */
  formatter: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,

  size: PropTypes.oneOf(['basic', 'small']),
  tip: PropTypes.element,
  mask: PropTypes.string,
  withTitle: PropTypes.bool,
  withFullMask: PropTypes.bool,

  shouldIgnoreCursor: PropTypes.bool,

  enableAutoComplete: PropTypes.bool,
  autoCompleteName: PropTypes.string,

  autoComplete: deprecatedPropType(
    PropTypes.string,
    'Use enableAutoComplete and autoCompleteName instead'
  ),
  icon: PropTypes.node,
};

export default Input;

export const InputWithRef = React.forwardRef((props, ref) => {
  return <Input {...props} forwardedRef={ref} />;
});

export const ReduxFormInput = ({ meta: { valid, touched, initial }, input, ...rest }) => {
  return <Input {...input} initial={initial} isError={!valid && touched} {...rest} />;
};

export const ReduxFormInputWithForwardRef = React.forwardRef(
  ({ meta: { valid, touched, initial }, input, ...rest }, ref) => {
    return (
      <Input
        {...input}
        initial={initial}
        isError={!valid && touched}
        {...rest}
        forwardedRef={ref}
      />
    );
  }
);
